import React, { useCallback, useEffect, useRef, useState } from "react";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import { type Iti } from "intl-tel-input";
import { type Option } from "~/components/select";
import { TextInput } from "~/components/text_input";
import { CountryInput } from "~/components/country_input";
import { useTranslation } from "react-i18next";
import "intl-tel-input/styles";
import "./phone_input.scss";

interface Props {
  id?: string;
  label?: string | null;
  outsideLabel?: boolean;
  maxLength?: number;
  required?: boolean;
  help?: React.ReactNode | null;
  countryError: string | null | undefined;
  phoneError: string | null | undefined;
  value: string | null;
  onChange: (phone: string) => void;
  onError: (error: PhoneError | null) => void;
  onFocus?: () => void;
}

export type PhoneError = "invalid" | "missing_country" | "too_short" | "too_long" | "unknown";

const convertCodeToError = (code: number | undefined): PhoneError => {
  switch (code) {
    case 0:
      return "invalid";
    case 1:
      return "missing_country";
    case 2:
      return "too_short";
    case 3:
      return "too_long";
    case 4:
      // This error actually means "local number", e.g. the number was provided
      // as 666-7777 instead of 555-666-7777
      return "too_short";
    default:
      return "unknown";
  }
};

export const PhoneInput = (props: Props) => {
  const {
    id,
    label,
    outsideLabel = false,
    maxLength,
    required = false,
    help,
    countryError,
    phoneError,
    value,
    onChange,
    onError,
    onFocus,
  } = props;
  const [country, setCountry] = useState<Option | null>(null);
  const itiRef = useRef<Iti | null>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("job_post");

  useEffect(() => {
    if (phoneRef.current) {
      itiRef.current = intlTelInput(phoneRef.current, {
        autoPlaceholder: "off",
        strictMode: true,
      });
    }

    return () => {
      itiRef.current?.destroy();
    };
  }, []);

  const onPhoneChange = useCallback(() => {
    const number = itiRef.current?.getNumber() || "";
    onChange(number);

    if (itiRef.current?.isValidNumber()) {
      onError(null);
    } else {
      onError(convertCodeToError(itiRef.current?.getValidationError()));
    }
  }, [onChange, onError]);

  const onCountryChange = useCallback(
    (option: Option | null) => {
      setCountry(option);

      if (option) {
        itiRef.current?.setCountry(option.value.toString());
      }

      onPhoneChange();
      phoneRef.current?.focus();
    },
    [setCountry, onPhoneChange]
  );

  const syncCountryFromPlugin = useCallback(() => {
    const selectedCountryData = itiRef.current?.getSelectedCountryData() || {};
    const { iso2, name } = selectedCountryData;

    if (iso2 && name) {
      setCountry({ value: iso2, label: name });
    } else {
      setCountry(null);
    }
  }, [setCountry]);

  useEffect(() => {
    // Store a reference to the current phone ref, otherwise it will be null in the cleanup function
    const phoneRefCurrent = phoneRef.current;

    // The plugin can detect a country change in a few scenarios:
    //  a) The user typed an explicit dial code before entering their number, like "+44"
    //  b) The user selected the US but then entered a Canadian area code, etc.
    //  c) Triggered via iti.setCountry() above, but basically a no-op in this case
    phoneRefCurrent?.addEventListener("countrychange", syncCountryFromPlugin);

    return () => {
      phoneRefCurrent?.removeEventListener("countrychange", syncCountryFromPlugin);
    };
  }, [syncCountryFromPlugin, onPhoneChange]);

  // The text input managed by the intl-tel-input plugin is uncontrolled, so the value prop we
  // receive is not normally relevant. However to support QuickApply, we do have to accept the
  // value prop and detect if there is a difference so we can tell the underlying plugin about it.
  useEffect(() => {
    if (typeof value === "string" && itiRef.current && value !== itiRef.current.getNumber()) {
      itiRef.current.setNumber(value);
    }
  }, [value]);

  return (
    <fieldset className="phone-input">
      <legend className="visually-hidden">{label}</legend>

      <div className="phone-input__country">
        <CountryInput
          id="country"
          label={t("application.country")}
          outsideLabel={outsideLabel}
          value={country}
          required={required}
          onChange={onCountryChange}
          error={countryError}
        />
      </div>

      <div className="phone-input__phone">
        <TextInput
          innerRef={phoneRef}
          id={id}
          type="tel"
          label={label}
          outsideLabel={outsideLabel}
          required={required}
          help={help}
          error={phoneError}
          maxLength={maxLength}
          onFocus={onFocus}
          onInput={onPhoneChange}
        />
      </div>
    </fieldset>
  );
};
